import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Lock, Play } from "lucide-react";
import type { RhythmPathMission, RhythmSubMission } from "../../lib/rhythm/rhythmPathPlan";
import {
  formatFingerPatternLabel,
  formatPitchPatternLabel,
  countSoundHits,
  RHYTHM_STEP_KIND_LABEL,
} from "../../lib/rhythm/rhythmPathPlan";
import { isRhythmSubMissionUnlocked } from "../../lib/rhythm/rhythmPathProgress";
import { BEGINNER_PATH_THEME as PATH } from "../../lib/beginnerPath/beginnerPathTheme";

export interface RhythmSubMissionMenuProps {
  mission: RhythmPathMission;
  completedSubMissionIds: string[];
  preview?: boolean;
  onSelectStep: (stepIndex: number) => void;
  onBack: () => void;
}

type StepBlock = {
  id: string;
  label: string;
  items: { step: RhythmSubMission; index: number }[];
};

function groupByBlock(subMissions: RhythmSubMission[]): StepBlock[] {
  const blocks: StepBlock[] = [];
  for (let i = 0; i < subMissions.length; i++) {
    const step = subMissions[i];
    const label = step.blockLabel ?? "Pasos";
    const last = blocks[blocks.length - 1];
    if (last && last.label === label) {
      last.items.push({ step, index: i });
    } else {
      blocks.push({
        id: `block-${blocks.length}-${label}`,
        label,
        items: [{ step, index: i }],
      });
    }
  }
  return blocks;
}

/** Primer bloque con algún paso pendiente; si todo está hecho, el último. */
function getActiveBlockId(
  blocks: StepBlock[],
  completedSubMissionIds: string[]
): string {
  const idx = blocks.findIndex((b) =>
    b.items.some((it) => !completedSubMissionIds.includes(it.step.id))
  );
  if (idx < 0) return blocks[blocks.length - 1]?.id ?? "";
  return blocks[idx].id;
}

/**
 * Submenú de pasos en acordeón por bloques.
 * Al completar un bloque se abre el siguiente; también se puede abrir/cerrar a mano.
 */
export function RhythmSubMissionMenu({
  mission,
  completedSubMissionIds,
  preview = false,
  onSelectStep,
  onBack,
}: RhythmSubMissionMenuProps) {
  const blocks = useMemo(
    () => groupByBlock(mission.subMissions),
    [mission.subMissions]
  );

  const activeBlockId = useMemo(
    () => getActiveBlockId(blocks, completedSubMissionIds),
    [blocks, completedSubMissionIds]
  );

  const [openIds, setOpenIds] = useState<Set<string>>(
    () => new Set(activeBlockId ? [activeBlockId] : [])
  );

  // Al avanzar (nuevo bloque activo), ábrelo automáticamente.
  useEffect(() => {
    if (!activeBlockId) return;
    setOpenIds((prev) => {
      if (prev.has(activeBlockId)) return prev;
      const next = new Set(prev);
      next.add(activeBlockId);
      return next;
    });
  }, [activeBlockId, mission.id]);

  // Reset al cambiar de misión.
  useEffect(() => {
    setOpenIds(new Set(activeBlockId ? [activeBlockId] : []));
    // solo al cambiar misión
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission.id]);

  const toggleBlock = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col w-full px-3 py-2 sm:px-5 lg:px-6">
      <header className="mb-4 flex flex-col gap-3 sm:mb-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.14em] text-[#99f6e4]/70">
            {mission.eyebrow}
          </p>
          <h1 className="mt-1 font-serif text-2xl font-light text-white/90 sm:text-3xl">
            {mission.title}
          </h1>
          <p className="mt-1 text-sm text-white/50">{mission.subtitle}</p>
          <p className="mt-2 text-sm text-white/65 sm:mt-3">{mission.goal}</p>
        </div>
        <p className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-white/40 sm:pb-1">
          {mission.subMissions.length} pasos · {blocks.length} bloques
        </p>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto pb-3 space-y-2.5 sm:space-y-3">
        {blocks.map((block) => {
          const doneCount = block.items.filter((it) =>
            completedSubMissionIds.includes(it.step.id)
          ).length;
          const total = block.items.length;
          const blockDone = doneCount === total;
          const isOpen = openIds.has(block.id);
          const isActive = block.id === activeBlockId;

          return (
            <section
              key={block.id}
              className="rounded-2xl border overflow-hidden"
              style={{
                borderColor: blockDone
                  ? PATH.cardBorderMint
                  : isActive
                    ? "rgba(153, 246, 228, 0.28)"
                    : PATH.cardBorder,
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <button
                type="button"
                onClick={() => toggleBlock(block.id)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-white/[0.03] sm:px-4"
              >
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-white/45 transition-transform duration-200 ${
                    isOpen ? "rotate-0" : "-rotate-90"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-[11px] uppercase tracking-[0.14em] text-white/70">
                      {block.label}
                    </h2>
                    {blockDone && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
                        <Check className="h-3 w-3" /> Completo
                      </span>
                    )}
                    {isActive && !blockDone && (
                      <span className="rounded-md bg-[#99f6e4]/12 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#99f6e4]">
                        Actual
                      </span>
                    )}
                  </div>
                </div>
                <span className="shrink-0 tabular-nums text-[11px] text-white/40">
                  {doneCount}/{total}
                </span>
              </button>

              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                  <ul className="grid grid-cols-1 gap-2.5 border-t border-white/[0.06] px-3 pb-3 pt-2.5 sm:grid-cols-2 sm:px-4 xl:grid-cols-3 2xl:grid-cols-4">
                    {block.items.map(({ step, index: i }) => {
                      const done = completedSubMissionIds.includes(step.id);
                      const unlocked = isRhythmSubMissionUnlocked(
                        mission,
                        i,
                        completedSubMissionIds,
                        preview
                      );
                      const pattern = formatFingerPatternLabel(step);
                      const pitches = formatPitchPatternLabel(step);
                      const soundHits = countSoundHits(step);
                      const isLong =
                        step.kind === "resistencia" ||
                        step.kind === "examen" ||
                        Boolean(step.isEndurance);

                      return (
                        <li key={step.id} className="min-w-0">
                          <button
                            type="button"
                            disabled={!unlocked}
                            onClick={() => onSelectStep(i)}
                            className="flex h-full w-full flex-col rounded-xl border px-3.5 py-3 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:bg-white/[0.04]"
                            style={{
                              borderColor: done
                                ? PATH.cardBorderMint
                                : isLong
                                  ? step.kind === "examen"
                                    ? "rgba(251, 191, 36, 0.35)"
                                    : "rgba(153, 246, 228, 0.28)"
                                  : PATH.cardBorder,
                              background: done
                                ? "rgba(153, 246, 228, 0.08)"
                                : isLong
                                  ? step.kind === "examen"
                                    ? "rgba(251, 191, 36, 0.06)"
                                    : "rgba(153, 246, 228, 0.05)"
                                  : "rgba(0,0,0,0.15)",
                            }}
                          >
                            <div className="flex items-start gap-2.5 min-w-0">
                              <span
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold"
                                style={{
                                  background: isLong
                                    ? step.kind === "examen"
                                      ? "rgba(251, 191, 36, 0.18)"
                                      : "rgba(153, 246, 228, 0.18)"
                                    : "rgba(153, 246, 228, 0.15)",
                                  color:
                                    step.kind === "examen"
                                      ? "#fde68a"
                                      : "#99f6e4",
                                }}
                              >
                                {unlocked ? (
                                  i + 1
                                ) : (
                                  <Lock className="h-3.5 w-3.5" />
                                )}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <h3 className="font-serif text-[15px] leading-snug text-white/90">
                                    {step.title}
                                  </h3>
                                  {done ? (
                                    <span className="inline-flex shrink-0 text-emerald-400/90">
                                      <Check className="h-3.5 w-3.5" />
                                    </span>
                                  ) : unlocked ? (
                                    <span className="inline-flex shrink-0 text-[#99f6e4]/70">
                                      <Play
                                        className="h-3.5 w-3.5"
                                        fill="currentColor"
                                      />
                                    </span>
                                  ) : null}
                                </div>
                                {isLong && step.kind && (
                                  <span
                                    className={`mt-1 inline-block rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] ${
                                      step.kind === "examen"
                                        ? "bg-amber-400/20 text-amber-200/95"
                                        : "bg-[#99f6e4]/15 text-[#99f6e4]"
                                    }`}
                                  >
                                    {RHYTHM_STEP_KIND_LABEL[step.kind]}
                                  </span>
                                )}
                                {step.subtitle && (
                                  <p className="mt-1 text-xs text-white/45 line-clamp-2">
                                    {step.subtitle}
                                  </p>
                                )}
                                <p className="mt-1.5 truncate text-[11px] font-mono text-white/35">
                                  {pattern}
                                  {pitches ? ` · ${pitches}` : ""}
                                  {soundHits ? ` · ${soundHits} golpes` : ""}
                                </p>
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onBack}
        className="mt-2 w-full shrink-0 py-2.5 rounded-xl text-sm text-white/50 hover:text-white/80 sm:max-w-xs"
      >
        Volver a misiones
      </button>
    </div>
  );
}
